/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Feb 14 2024
 **/

import { useCallback } from "react";

import { useStompManager } from "@/frameworks/libraries/network";
import { Properties } from "@/frameworks/libraries/properties";
import { ChronoUtils, Configure } from "@/frameworks/libraries/utilities";

import { useAccount, useVoiceCall } from "@/frameworks/core/API";
import { useDMRoomListState } from "@/frameworks/core/chat";
import { usePhoneCall, useRoomViewActions } from "@/frameworks/core/hooks";
import { useUnityCommand } from "@/frameworks/core/unity";

const LOGTAG = "#useAccountActions#";

export default function useAccountActions() {
	const accountAPI = useAccount();
	const unityCommand = useUnityCommand();
	const phoneCall = usePhoneCall();
	const dmRoomList = useDMRoomListState();
	const voiceCallAPI = useVoiceCall();
	const { roomViewCloseAll } = useRoomViewActions();
	const stompNetwork = useStompManager();

	const setTimezone = (timezone?: string) => {
		ChronoUtils.set_timezone(timezone);
	};

	const handleBeforeLogout = async () => {
		Configure.currentConference = null;
		if (dmRoomList) {
			dmRoomList.filter((room) => {
				if (room.inVoiceChat) {
					// Update 04/02/2025: Disable function call leave voice call when logout process
					// voiceCallAPI.leave.execute({ roomId: room.roomId });
				}
			});
		}
		await phoneCall.allCallEnding();
	};

	const logout = useCallback(() => {
		// TODO: useServiceInitialize.ts의 deviceKey 참고
		handleBeforeLogout()
			.then(() => {
				roomViewCloseAll();
				accountAPI.logout
					.execute({
						deviceKey: Properties.browserTabID
					})
					.then(async () => {
						unityCommand.logout();
						Configure.clear_authenticated();
						console.debug(LOGTAG, "logout success");

						if (stompNetwork.isConnected) {
							await stompNetwork.disconnect();
						}
						if (location.pathname.includes("/admin")) {
							window.location.replace("/login/admin");
						} else {
							window.location.replace("/");
						}
					});
			})
			.catch((error) => {
				console.error(LOGTAG, "Logout failed with", error);
			});
	}, [phoneCall, voiceCallAPI]);

	return { logout, setTimezone };
}
